import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Grid,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Visibility,
  CheckCircle,
  Cancel,
  Description,
  DirectionsCar,
} from '@mui/icons-material';
import apiService from '../services/apiService';
import config from '../config/config';

const VerificationsPage = () => {
  const [pendingDrivers, setPendingDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [viewImageUrl, setViewImageUrl] = useState('');
  const [openImageViewer, setOpenImageViewer] = useState(false);

  useEffect(() => {
    fetchPendingVerifications();
  }, []);

  const fetchPendingVerifications = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPendingVerifications();

      if (response.success) {
        setPendingDrivers(response.data || []);
      } else {
        setError(response.message || 'Failed to load pending verifications');
        setPendingDrivers([]);
      }
    } catch (err) {
      console.error('Verifications error:', err);
      setError('Failed to load pending verifications');
      setPendingDrivers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDriver = (driver) => {
    setSelectedDriver(driver);
    setRemarks('');
    setOpenDialog(true);
  };

  const handleViewDocument = (documentUrl) => {
    // Remove leading slash if present to avoid double slashes
    const cleanUrl = documentUrl.startsWith('/') ? documentUrl.substring(1) : documentUrl;
    const fullUrl = `${config.SOCKET_URL}/${cleanUrl}`;
    setViewImageUrl(fullUrl);
    setOpenImageViewer(true);
  };

  const handleCloseImageViewer = () => {
    setOpenImageViewer(false);
    setViewImageUrl('');
  };

  const handleVerifyDocument = async (documentTypeName, documentTypeId, status) => {
    if (!selectedDriver) return;

    if (!remarks && status === 'rejected') {
      alert('Please provide remarks for rejection');
      return;
    }

    console.log('=== VERIFY DOCUMENT (Frontend) ===');
    console.log('Driver ID:', selectedDriver.id);
    console.log('Document Type Name:', documentTypeName);
    console.log('Document Type ID:', documentTypeId);
    console.log('Status:', status);
    console.log('Remarks:', remarks);

    try {
      setActionLoading(true);
      const response = await apiService.verifyDriverDocument(
        selectedDriver.id,
        documentTypeId,  // Send the ID instead of the name
        status,
        remarks
      );

      if (response.success) {
        // Update the selected driver's document status immediately in the UI
        setSelectedDriver(prevDriver => ({
          ...prevDriver,
          documents: {
            ...prevDriver.documents,
            [documentTypeName]: {
              ...prevDriver.documents[documentTypeName],
              status: status
            }
          }
        }));

        alert(`Document ${status} successfully`);

        // Refresh the pending verifications list in the background
        fetchPendingVerifications();
      } else {
        alert(response.message || `Failed to ${status} document`);
      }
    } catch (err) {
      alert(`Failed to ${status} document`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerifyDriver = async () => {
    if (!selectedDriver) return;

    // Check if all documents are approved
    const allDocuments = Object.values(selectedDriver.documents);
    const allApproved = allDocuments.every(doc => doc.status === 'verified');

    if (!allApproved) {
      alert('Please approve all documents before verifying the driver');
      return;
    }

    if (!window.confirm('Are you sure you want to verify this driver?')) {
      return;
    }

    try {
      setActionLoading(true);
      const response = await apiService.verifyDriver(selectedDriver.id);

      if (response.success) {
        alert('Driver verified successfully');
        setOpenDialog(false);
        fetchPendingVerifications();
      } else {
        alert(response.message || 'Failed to verify driver');
      }
    } catch (err) {
      alert('Failed to verify driver');
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Driver Name',
      width: 180,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {params.row.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.phone}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'vehicleType',
      headerName: 'Vehicle',
      width: 150,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">{params.row.vehicleType}</Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.vehicleNumber}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'registeredDate',
      headerName: 'Registered',
      width: 130,
    },
    {
      field: 'documentsCount',
      headerName: 'Documents',
      width: 120,
      renderCell: (params) => {
        const docCount = Object.keys(params.row.documents).length;
        return (
          <Chip
            label={`${docCount} docs`}
            color="warning"
            size="small"
            icon={<Description />}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          startIcon={<Visibility />}
          onClick={() => handleViewDriver(params.row)}
        >
          Review
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Pending Verifications
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Review and verify driver documents
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && pendingDrivers.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No pending verifications at the moment
        </Alert>
      )}

      <Card>
        <CardContent>
          <DataGrid
            rows={pendingDrivers}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            autoHeight
            loading={loading}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Review Driver Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DirectionsCar />
            <Typography variant="h6">Review Driver Documents</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedDriver && (
            <Box sx={{ pt: 2 }}>
              {/* Driver Info */}
              <Card sx={{ mb: 3, bgcolor: 'primary.main', color: 'text.primary' }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption">Driver Name</Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {selectedDriver.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption">Phone</Typography>
                      <Typography variant="body1">{selectedDriver.phone}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption">Vehicle Type</Typography>
                      <Typography variant="body1">{selectedDriver.vehicleType}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption">Vehicle Number</Typography>
                      <Typography variant="body1">{selectedDriver.vehicleNumber}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Documents */}
              <Typography variant="h6" gutterBottom>
                Documents to Review
              </Typography>
              <List>
                {Object.entries(selectedDriver.documents).map(([docType, doc], index) => (
                  <React.Fragment key={docType}>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" fontWeight={600}>
                              {docType.toUpperCase()}
                            </Typography>
                            {doc.status === 'verified' && (
                              <Chip label="Verified" color="success" size="small" />
                            )}
                            {doc.status === 'rejected' && (
                              <Chip label="Rejected" color="error" size="small" />
                            )}
                            {doc.status === 'pending' && (
                              <Chip label="Pending" color="warning" size="small" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleViewDocument(doc.url)}
                              sx={{ mr: 1 }}
                            >
                              View Document
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircle />}
                              onClick={() => handleVerifyDocument(docType, doc.documentTypeId, 'verified')}
                              disabled={actionLoading || doc.status === 'verified'}
                              sx={{ mr: 1 }}
                            >
                              {doc.status === 'verified' ? 'Approved' : 'Approve'}
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              startIcon={<Cancel />}
                              onClick={() => handleVerifyDocument(docType, doc.documentTypeId, 'rejected')}
                              disabled={actionLoading || doc.status === 'rejected'}
                            >
                              {doc.status === 'rejected' ? 'Rejected' : 'Reject'}
                            </Button>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < Object.entries(selectedDriver.documents).length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>

              {/* Remarks */}
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Remarks (optional - for internal notes)"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                sx={{ mt: 2 }}
              />

              {/* Verify Driver Button */}
              <Box sx={{ mt: 3 }}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={<CheckCircle />}
                  onClick={handleVerifyDriver}
                  disabled={actionLoading}
                  sx={{
                    bgcolor: '#FFC107',
                    color: '#000',
                    '&:hover': {
                      bgcolor: '#FFD54F',
                    },
                    '&:disabled': {
                      bgcolor: '#e0e0e0',
                      color: '#9e9e9e',
                    },
                  }}
                >
                  Verify Driver
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                  All documents must be approved before verifying the driver
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={actionLoading}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Preview Modal */}
      <Dialog
        open={openImageViewer}
        onClose={handleCloseImageViewer}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Document Preview</DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
              bgcolor: '#f5f5f5',
              borderRadius: 1,
              p: 2
            }}
          >
            {viewImageUrl && (
              <img
                src={viewImageUrl}
                alt="Document"
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: '4px'
                }}
                onError={(e) => {
                  console.error('Image failed to load:', viewImageUrl);
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjE4IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+SW1hZ2UgTm90IEZvdW5kPC90ZXh0Pjwvc3ZnPg==';
                  e.target.style.maxWidth = '200px';
                }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseImageViewer}
            variant="contained"
            sx={{
              bgcolor: '#FFC107',
              color: '#000',
              '&:hover': {
                bgcolor: '#FFD54F',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VerificationsPage;
